import { useEffect, useState } from "react";
import { toast } from "sonner";
import { WifiOff, RefreshCw, Check } from "lucide-react";
import { flushQueue, queueCount, onQueueChange } from "@/lib/offlineQueue";
import { useT } from "@/i18n";

// Shows connection / pending-sync status AND drives background sync of any
// entries logged while offline. Mounted inside AppShell so it runs on every
// authenticated screen.
export function OfflineIndicator() {
  const t = useT();
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [pending, setPending] = useState(queueCount());
  const [syncing, setSyncing] = useState(false);

  const runSync = async () => {
    if (!navigator.onLine || queueCount() === 0) return;
    setSyncing(true);
    const n = await flushQueue();
    setSyncing(false);
    setPending(queueCount());
    if (n > 0) toast.success(t("Synced {n} offline {entry}").replace("{n}", n).replace("{entry}", n === 1 ? t("entry") : t("entries")));
  };

  useEffect(() => {
    const goOnline = () => { setOnline(true); runSync(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    const off = onQueueChange((c) => setPending(c));
    runSync(); // attempt on mount
    const iv = setInterval(runSync, 30000);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      off();
      clearInterval(iv);
    };
  }, []); // eslint-disable-line

  if (online && pending === 0) return null;

  return (
    <button
      type="button"
      onClick={runSync}
      data-testid="offline-indicator"
      title={online ? t("Tap to sync now") : t("You're offline")}
      className={`flex items-center gap-1.5 px-2.5 h-8 rounded-full text-xs font-bold transition-colors ${
        online ? "bg-amber-400/20 text-amber-300 hover:bg-amber-400/30" : "bg-red-500/20 text-red-300"
      }`}
    >
      {!online ? (
        <><WifiOff size={14} /> <span className="hidden sm:inline">{t("Offline")}</span></>
      ) : syncing ? (
        <><RefreshCw size={14} className="animate-spin" /> <span className="hidden sm:inline">{t("Syncing…")}</span></>
      ) : (
        <><Check size={14} /></>
      )}
      {pending > 0 && (
        <span data-testid="offline-pending-count" className="min-w-[18px] text-center bg-white/90 text-ink rounded-full px-1 leading-4">
          {pending}
        </span>
      )}
    </button>
  );
}
